// Shared atoms: Badge, Pill, IconButton, sparklines and charts (SVG, no library).
// Migrated from working dashboard to TypeScript

import React from 'react';

export const cx = (...xs: (string | boolean | undefined | null)[]): string => 
  xs.filter(Boolean).join(' ');

// ── Status tokens from Figma palette ────────────────────────────────────────
export const TONE = {
  ok: { bg: '#C6F3D8', fg: '#159D4C', ring: '#159D4C' },
  warn: { bg: '#FDF9D3', fg: '#CA9401', ring: '#F9C12E' },
  crit: { bg: '#FDD3D3', fg: '#F40B0B', ring: '#F40B0B' },
  info: { bg: '#DDF4FF', fg: '#0A69DA', ring: '#0A69DA' },
  muted: { bg: '#EEEFF2', fg: '#6B7280', ring: '#9CA3AF' },
  draft: { bg: '#EDE5FC', fg: '#7F56D9', ring: '#7F56D9' },
} as const;

interface BadgeProps {
  tone?: keyof typeof TONE;
  children: React.ReactNode;
  dot?: boolean;
  className?: string;
}

export function Badge({ tone = 'muted', children, dot = false, className }: BadgeProps) {
  const t = TONE[tone] || TONE.muted;
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-[4px] px-2 py-[3px]',
        'font-display text-[12px] font-semibold whitespace-nowrap',
        className
      )}
      style={{ background: t.bg, color: t.fg }}
    >
      {dot && (
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: t.ring }} />
      )}
      {children}
    </span>
  );
}

interface StatusDotProps {
  tone?: keyof typeof TONE;
  size?: number;
}

export function StatusDot({ tone = 'ok', size = 8 }: StatusDotProps) {
  const t = TONE[tone] || TONE.ok;
  return (
    <span
      className="inline-block rounded-full shrink-0"
      style={{ width: size, height: size, background: t.ring }}
    />
  );
}

interface DeltaTagProps {
  delta: number | null | undefined;
  unit?: string;
  direction?: 'improving' | 'worsening' | 'flat';
}

export function DeltaTag({ delta, unit, direction }: DeltaTagProps) {
  if (delta == null) return null;
  const isAbs = unit === 'abs';
  const sign = delta > 0 ? '+' : '';
  const dir = direction || (delta === 0 ? 'flat' : delta > 0 ? 'worsening' : 'improving');
  // Arrow always follows the *sign of movement* — color tells the story of good/bad.
  const arrow = delta === 0 ? '→' : delta > 0 ? '↑' : '↓';
  // Goodness from explicit direction; default-higher-is-better for unsigned KPIs.
  const goodUp = direction == null;
  const isGood = goodUp ? delta > 0 : dir === 'improving';
  const isBad = goodUp ? delta < 0 : dir === 'worsening';
  const tone = dir === 'flat' ? 'muted' : isGood ? 'ok' : isBad ? 'crit' : 'muted';
  return (
    <Badge tone={tone}>
      <span className="font-data text-[11px]">
        {arrow} {sign}
        {isAbs ? delta : `${delta}%`}
      </span>
    </Badge>
  );
}

// ── Sparkline ───────────────────────────────────────────────────────────────
interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  fill?: boolean;
}

export function Sparkline({ data, width = 140, height = 36, color = '#FA500E', fill = true }: SparklineProps) {
  if (!data || !data.length) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => [i * step, height - ((v - min) / range) * (height - 4) - 2]);
  const d = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(2)} ${p[1].toFixed(2)}`)
    .join(' ');
  const area = `${d} L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="block"
      role="img"
      aria-label="Chart"
    >
      {fill && <path d={area} fill={color} opacity="0.10" />}
      <path
        d={d}
        stroke={color}
        strokeWidth="1.75"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.5" fill={color} />
    </svg>
  );
}

// ── KPI tile ────────────────────────────────────────────────────────────────
interface KpiTileData {
  id?: string;
  label: string;
  sublabel?: string;
  value: string | number;
  unit?: string;
  delta?: number;
  deltaUnit?: string;
  deltaDirection?: 'improving' | 'worsening' | 'flat';
  deltaLabel?: string;
  denominator?: string;
  spark?: number[];
}

interface KpiTileProps {
  tile: KpiTileData;
  onClick?: () => void;
  accent?: string;
}

export function KpiTile({ tile, onClick, accent = '#FA500E' }: KpiTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'group flex flex-col gap-3 text-left',
        'rounded-[10px] bg-white border border-[#F3E4C8]',
        'p-5 transition hover:border-ink/30 hover:shadow-[0_2px_3px_0_rgba(0,0,0,0.06)] focus:outline-none focus:ring-2 focus:ring-[#FA500E]/30'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display text-[12px] font-bold uppercase tracking-[0.04em] text-ink/60">
            {tile.label}
          </div>
          <div className="font-display text-[12px] text-ink/50 mt-1">{tile.sublabel}</div>
        </div>
        <DeltaTag delta={tile.delta} unit={tile.deltaUnit} direction={tile.deltaDirection} />
      </div>
      <div className="flex items-end justify-between gap-3 mt-1">
        <div className="flex items-baseline gap-1">
          <span
            className="font-display font-bold text-ink"
            style={{ fontSize: 36, lineHeight: '1' }}
          >
            {tile.value}
          </span>
          {tile.unit && (
            <span className="font-display text-[18px] font-semibold text-ink/60">{tile.unit}</span>
          )}
        </div>
        <Sparkline data={tile.spark || []} color={accent} />
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-[#F7EDDB]">
        <span className="font-data text-[12px] text-ink/55">{tile.denominator}</span>
        <span className="font-data text-[12px] text-ink/40">{tile.deltaLabel}</span>
      </div>
    </button>
  );
}

// ── Area trend chart ────────────────────────────────────────────────────────
interface ChartSeries {
  name: string;
  data: number[];
  color?: string;
}

interface AreaTrendData {
  series: ChartSeries[];
  labels: string[];
}

interface AreaTrendProps {
  data: AreaTrendData;
  height?: number;
  palette?: string[];
}

export function AreaTrend({ data, height = 260, palette = ['#FA500E', '#7F56D9'] }: AreaTrendProps) {
  const w = 720,
    padL = 36,
    padR = 12,
    padT = 16,
    padB = 28;
  const innerW = w - padL - padR;
  const innerH = height - padT - padB;
  const series = data.series;
  const labels = data.labels;
  const n = labels.length;
  const xs = (i: number) => padL + (innerW * i) / (n - 1);
  const allValues = series.flatMap((s) => s.data);
  const minV = Math.min(...allValues) - 4;
  const maxV = Math.max(...allValues) + 4;
  const ys = (v: number) => padT + innerH - ((v - minV) / (maxV - minV)) * innerH;

  return (
    <svg
      viewBox={`0 0 ${w} ${height}`}
      className="w-full"
      preserveAspectRatio="none"
      role="img"
      aria-label="Area Chart"
    >
      {/* gridlines */}
      {[0, 0.25, 0.5, 0.75, 1].map((g, i) => (
        <line
          key={g}
          x1={padL}
          x2={w - padR}
          y1={padT + innerH * g}
          y2={padT + innerH * g}
          stroke="#F3E4C8"
          strokeDasharray={i === 4 ? '0' : '3 3'}
          strokeWidth="1"
        />
      ))}
      {/* y labels */}
      {[0, 0.5, 1].map((g) => {
        const v = Math.round(maxV - (maxV - minV) * g);
        return (
          <text
            key={g}
            x={padL - 8}
            y={padT + innerH * g + 4}
            textAnchor="end"
            fontSize="11"
            fontFamily="Lato"
            fill="#6B7280"
          >
            {v}%
          </text>
        );
      })}
      {/* series */}
      {series.map((s, si) => {
        const color = s.color || palette[si % palette.length];
        const pts = s.data.map((v, i) => [xs(i), ys(v)]);
        const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');
        const area = `${d} L ${w - padR} ${padT + innerH} L ${padL} ${padT + innerH} Z`;
        return (
          <g key={s.name}>
            <path d={area} fill={color} opacity="0.08" />
            <path d={d} stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" />
            {pts.map((p, i) => (
              <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill={color} />
            ))}
          </g>
        );
      })}
      {/* x labels */}
      {labels.map((label, i) => (
        <text
          key={label}
          x={xs(i)}
          y={height - 8}
          textAnchor="middle"
          fontSize="11"
          fontFamily="Lato"
          fill="#6B7280"
        >
          {label}
        </text>
      ))}
    </svg>
  );
}

// ── Donut ───────────────────────────────────────────────────────────────────
interface DonutData {
  value: number;
  color: string;
}

interface DonutProps {
  data: DonutData[];
  size?: number;
  thickness?: number;
  label?: string;
}

export function Donut({ data, size = 180, thickness = 18, label = 'TOTAL' }: DonutProps) {
  const total = data.reduce((a, d) => a + d.value, 0);
  const r = (size - thickness) / 2;
  const C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Donut Chart">
      <g transform={`translate(${size / 2}, ${size / 2}) rotate(-90)`}>
        <circle r={r} fill="none" stroke="#F7EDDB" strokeWidth={thickness} />
        {data.map((d) => {
          const frac = d.value / total;
          const dash = C * frac;
          const offset = -acc * C;
          acc += frac;
          return (
            <circle
              key={`donut-${d.color}-${d.value}`}
              r={r}
              fill="none"
              stroke={d.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${C - dash}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
            />
          );
        })}
      </g>
      <text
        x={size / 2}
        y={size / 2 - 4}
        textAnchor="middle"
        fontFamily="Raleway"
        fontWeight="700"
        fontSize="28"
        fill="#121111"
      >
        {total}
      </text>
      <text
        x={size / 2}
        y={size / 2 + 16}
        textAnchor="middle"
        fontFamily="Raleway"
        fontWeight="600"
        fontSize="11"
        fill="#6B7280"
        letterSpacing="0.05em"
      >
        {label}
      </text>
    </svg>
  );
}

// ── Avatar with initials ────────────────────────────────────────────────────
interface AvatarProps {
  name: string;
  size?: number;
}

export function Avatar({ name, size = 24 }: AvatarProps) {
  const initials = (name || '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const hue = Array.from(name || '?').reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-display font-bold text-white shrink-0"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        background: `oklch(0.65 0.12 ${hue})`,
      }}
    >
      {initials}
    </span>
  );
}

// ── Lang dot ────────────────────────────────────────────────────────────────
export const LANG_COLOR: Record<string, string> = {
  TypeScript: '#3178C6',
  Go: '#00ADD8',
  Rust: '#DEA584',
  'C++': '#F34B7D',
  Astro: '#FF5D01',
  Python: '#3572A5',
  JavaScript: '#F1E05A',
};

interface LangDotProps {
  lang: string;
  withName?: boolean;
}

export function LangDot({ lang, withName = true }: LangDotProps) {
  return (
    <span className="inline-flex items-center gap-2 font-data text-[12px] text-ink/70">
      <span className="h-2 w-2 rounded-full" style={{ background: LANG_COLOR[lang] || '#999' }} />
      {withName && <span>{lang}</span>}
    </span>
  );
}

// ── Icons (inline SVG, 1.5 stroke, matching Untitled UI feel in Figma) ──────
interface IconProps {
  name: string;
  size?: number;
  className?: string;
  stroke?: string;
}

export const Icon = ({ name, size = 16, className, stroke = 'currentColor' }: IconProps) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
  };
  
  switch (name) {
    case 'dashboard':
      return (
        <svg {...common} role="img" aria-label="Dashboard Icon">
          <rect x="3" y="3" width="7" height="9" rx="1.5" />
          <rect x="14" y="3" width="7" height="5" rx="1.5" />
          <rect x="14" y="12" width="7" height="9" rx="1.5" />
          <rect x="3" y="16" width="7" height="5" rx="1.5" />
        </svg>
      );
    case 'book':
      return (
        <svg {...common} role="img" aria-label="Book Icon">
          <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v17H6.5A2.5 2.5 0 0 0 4 21.5z" />
          <path d="M4 21.5A2.5 2.5 0 0 1 6.5 19H20" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...common} role="img" aria-label="Shield Icon">
          <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case 'package':
      return (
        <svg {...common} role="img" aria-label="Package Icon">
          <path d="M21 8 12 3 3 8v8l9 5 9-5z" />
          <path d="m3 8 9 5 9-5" />
          <path d="M12 13v8" />
        </svg>
      );
    case 'tag':
      return (
        <svg {...common} role="img" aria-label="Tag Icon">
          <path d="M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9z" />
          <circle cx="8" cy="8" r="1.5" />
        </svg>
      );
    case 'users':
      return (
        <svg {...common} role="img" aria-label="Users Icon">
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6" />
          <path d="M16 4a3.5 3.5 0 0 1 0 7" />
          <path d="M18 21c0-2.5-1-4.5-3-5.5" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...common} role="img" aria-label="Settings Icon">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      );
    case 'search':
      return (
        <svg {...common} role="img" aria-label="Search Icon">
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      );
    case 'filter':
      return (
        <svg {...common} role="img" aria-label="Filter Icon">
          <path d="M3 5h18l-7 8v6l-4 2v-8z" />
        </svg>
      );
    case 'plus':
      return (
        <svg {...common} role="img" aria-label="Plus Icon">
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case 'chevron-down':
      return (
        <svg {...common} role="img" aria-label="Chevron Down Icon">
          <path d="m6 9 6 6 6-6" />
        </svg>
      );
    case 'chevron-right':
      return (
        <svg {...common} role="img" aria-label="Chevron Right Icon">
          <path d="m9 6 6 6-6 6" />
        </svg>
      );
    case 'file-text':
      return (
        <svg {...common} role="img" aria-label="File Text Icon">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14,2 14,8 20,8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...common} role="img" aria-label="Clock Icon">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12,6 12,12 16,14" />
        </svg>
      );
    case 'eye':
      return (
        <svg {...common} role="img" aria-label="Eye Icon">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'alert-triangle':
      return (
        <svg {...common} role="img" aria-label="Alert Triangle Icon">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <circle cx="12" cy="17" r="1" />
        </svg>
      );
    case 'external':
      return (
        <svg {...common} role="img" aria-label="External Icon">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15,3 21,3 21,9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      );
    case 'download':
      return (
        <svg {...common} role="img" aria-label="Download Icon">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7,10 12,15 17,10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      );
    case 'arrow-up-right':
      return (
        <svg {...common} role="img" aria-label="Arrow Up Right Icon">
          <line x1="7" y1="17" x2="17" y2="7" />
          <polyline points="7,7 17,7 17,17" />
        </svg>
      );
    default:
      // Fallback for unknown icons
      return (
        <svg {...common} role="img" aria-label="Icon">
          <circle cx="12" cy="12" r="10" />
          <text x="12" y="16" textAnchor="middle" fontSize="12" fill={stroke}>?</text>
        </svg>
      );
  }
};

export type { KpiTileData, BadgeProps, IconProps, SparklineProps };